import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import type { FishingSpotCreateOrUpdateRequest, FishingSpotDetail } from '@models/fishing-spots';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { ToastService } from '@services/toast.service';
import { BreadcrumbComponent, type BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';
import { ImagePickerComponent } from '@shared/components/image-picker/image-picker.component';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import { LocationPickerComponent } from '@shared/components/location-picker/location-picker.component';
import { firstValueFrom, of } from 'rxjs';
import { CombinationPricingComponent } from '../shared/combination-pricing/combination-pricing.component';

interface FishingSpotForm {
  number: FormControl<number | null>;
  spots: FormControl<number | null>;
  minimumFishermen: FormControl<number | null>;
  coTravelers: FormControl<number | null>;
  isActive: FormControl<boolean>;
  isDummy: FormControl<boolean>;
  dogsAllowed: FormControl<'inherit' | boolean>;
  latitude: FormControl<number>;
  longitude: FormControl<number>;
  descriptionNl: FormControl<string | null>;
  descriptionEn: FormControl<string | null>;
  descriptionDe: FormControl<string | null>;
  descriptionIt: FormControl<string | null>;
  descriptionFr: FormControl<string | null>;
}

@Component({
  selector: 'app-fishing-spot-form',
  templateUrl: './fishing-spot-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    LocationPickerComponent,
    BreadcrumbComponent,
    ImagePickerComponent,
    LocalizedTextEditorComponent,
    CombinationPricingComponent,
  ],
})
export class FishingSpotFormComponent implements OnInit {
  private readonly fishingSpotsService = inject(CrmFishingSpotsService);
  private readonly watersService = inject(CrmWatersService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly waterIdSig = signal(0);
  private readonly spotIdSig = signal<number | null>(null);

  readonly isEditMode = computed(() => this.spotIdSig() !== null);

  // Bindings for the combination-pricing section. In create mode the id is 0 until the spot's own
  // save returns; the child then PUTs the grid against the freshly-created id (see save()).
  readonly combinationWaterId = computed(() => this.waterIdSig());
  readonly combinationSpotId = computed(() => this.spotIdSig() ?? 0);

  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);
  readonly waterTitle = signal('');
  readonly spotTitle = signal('');
  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('nav.waters'), routerLink: ['/waters'] },
    {
      label: this.waterTitle() || String(this.waterIdSig()),
      routerLink: ['/waters/edit', this.waterIdSig()],
      queryParams: { tab: 2 },
    },
    {
      label:
        this.isEditMode() && this.spotTitle()
          ? this.spotTitle()
          : this.translate.instant('waters.fishing-spots.breadcrumb.create'),
    },
  ]);

  private readonly imagePicker = viewChild(ImagePickerComponent);
  private readonly combinationPricing = viewChild(CombinationPricingComponent);

  readonly pendingImageFile = signal<File | null>(null);
  readonly imageName = signal<string | null>(null);
  readonly imageUrl = signal<string | null>(null);

  readonly waterResource = rxResource({
    params: () => (this.waterIdSig() ? { waterId: this.waterIdSig() } : undefined),
    stream: ({ params }) => this.watersService.getWater(params.waterId),
  });

  readonly waterHintLat = computed(() => parseFloat(this.waterResource.value()?.latitude ?? '') || 0);
  readonly waterHintLng = computed(() => parseFloat(this.waterResource.value()?.longitude ?? '') || 0);

  readonly spotResource = rxResource({
    params: () => {
      const sid = this.spotIdSig();
      const wid = this.waterIdSig();
      return sid && wid ? { waterId: wid, spotId: sid } : undefined;
    },
    stream: ({ params }) => this.fishingSpotsService.getSpot(params.waterId, params.spotId),
  });

  readonly isLoading = this.spotResource.isLoading;

  readonly form = new FormGroup<FishingSpotForm>({
    number: new FormControl<number | null>(null, Validators.required),
    spots: new FormControl<number | null>(null, Validators.required),
    minimumFishermen: new FormControl<number | null>(null),
    coTravelers: new FormControl<number | null>(null, Validators.required),
    isActive: new FormControl(true, { nonNullable: true }),
    isDummy: new FormControl(false, { nonNullable: true }),
    dogsAllowed: new FormControl<'inherit' | boolean>('inherit', { nonNullable: true }),
    latitude: new FormControl(0, { nonNullable: true }),
    longitude: new FormControl(0, { nonNullable: true }),
    descriptionNl: new FormControl<string | null>(null, Validators.required),
    descriptionEn: new FormControl<string | null>(null),
    descriptionDe: new FormControl<string | null>(null),
    descriptionIt: new FormControl<string | null>(null),
    descriptionFr: new FormControl<string | null>(null),
  });

  constructor() {
    effect(() => {
      const water = this.waterResource.value();
      if (water) this.waterTitle.set(water.title);
    });

    effect(() => {
      const spot = this.spotResource.value();
      if (spot) this.patchForm(spot);
    });

    effect(() => {
      if (this.spotResource.error()) {
        this.toastService.showError(this.translate.instant('waters.fishing-spots.load-spot-failed'));
        this.navigateBack();
      }
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const spotIdParam = this.route.snapshot.paramMap.get('spotId');
    this.waterIdSig.set(Number(idParam));
    if (spotIdParam) this.spotIdSig.set(Number(spotIdParam));
  }

  private patchForm(spot: FishingSpotDetail): void {
    // A swim has no meaningful own name (its title defaults to the water's), so label it by number.
    this.spotTitle.set(this.translate.instant('waters.fishing-spots.swim-label', { number: spot.number ?? spot.id }));
    this.imageName.set(spot.imageName);
    this.imageUrl.set(spot.imageUrl);
    this.form.patchValue({
      number: spot.number,
      spots: spot.spots,
      minimumFishermen: spot.minimumFishermen,
      coTravelers: spot.coTravelers,
      isActive: spot.isActive,
      isDummy: spot.isDummy,
      dogsAllowed: spot.dogsAllowed === null ? 'inherit' : spot.dogsAllowed,
      latitude: spot.locationLat,
      longitude: spot.locationLong,
      descriptionNl: spot.descriptionNl,
      descriptionEn: spot.descriptionEn,
      descriptionDe: spot.descriptionDe,
      descriptionIt: spot.descriptionIt,
      descriptionFr: spot.descriptionFr,
    });
  }

  onImageFileSelected(file: File): void {
    this.pendingImageFile.set(file);
  }

  async save(): Promise<void> {
    // Validate the combination-pricing grid too (each cell must have both prices or neither).
    const pricingValid = this.combinationPricing()?.validate() ?? true;
    if (this.form.invalid || !pricingValid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const dto: FishingSpotCreateOrUpdateRequest = {
      number: raw.number,
      spots: raw.spots,
      minimumFishermen: raw.minimumFishermen,
      coTravelers: raw.coTravelers,
      isActive: raw.isActive,
      isDummy: raw.isDummy,
      dogsAllowed: raw.dogsAllowed === 'inherit' ? null : raw.dogsAllowed,
      locationLat: raw.latitude,
      locationLong: raw.longitude,
      descriptionNl: raw.descriptionNl || null,
      descriptionEn: raw.descriptionEn || null,
      descriptionDe: raw.descriptionDe || null,
      descriptionIt: raw.descriptionIt || null,
      descriptionFr: raw.descriptionFr || null,
    };

    this.isSaving.set(true);
    const spotId = this.spotIdSig();
    const waterId = this.waterIdSig();

    const request$ =
      this.isEditMode() && spotId !== null
        ? this.fishingSpotsService.updateSpot(waterId, spotId, dto)
        : this.fishingSpotsService.createSpot(waterId, dto);

    // Stage 1 — persist the fishing spot itself (create or update).
    let spot: FishingSpotDetail;
    try {
      spot = await firstValueFrom(request$);
    } catch {
      this.toastService.showError(this.translate.instant('waters.fishing-spots.save-failed'));
      this.isSaving.set(false);
      return;
    }

    // Stage 2 — fold in the combination-pricing grid. In create mode the spot only gets an id here, so
    // pass it through — the child PUTs the grid against the just-created spot.
    try {
      await firstValueFrom(this.combinationPricing()?.save(spot.id) ?? of(null));
    } catch {
      // The fishing spot itself saved; only the pricing PUT failed. Adopt the new id so a retry
      // updates this spot instead of creating a duplicate (no-op if we were already editing).
      this.spotIdSig.set(spot.id);
      this.toastService.showError(this.translate.instant('waters.combination-prices.save-failed'));
      this.isSaving.set(false);
      return;
    }

    const successKey = this.isEditMode()
      ? 'waters.fishing-spots.save-success-edit'
      : 'waters.fishing-spots.save-success-create';
    this.toastService.showSuccess(this.translate.instant(successKey));

    // Stage 3 — upload a pending image if one was picked. A failure here is non-fatal: the save already
    // succeeded, so warn but still navigate back.
    const pendingFile = this.pendingImageFile();
    if (pendingFile) {
      try {
        await firstValueFrom(this.fishingSpotsService.uploadImage(waterId, spot.id, pendingFile));
        this.pendingImageFile.set(null);
        this.imagePicker()?.clearPending();
      } catch {
        this.toastService.showError(this.translate.instant('waters.fishing-spots.upload-failed'));
      }
    }

    this.isSaving.set(false);
    this.navigateBack();
  }

  async delete(): Promise<void> {
    const spotId = this.spotIdSig();
    if (!spotId) return;

    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.fishing-spots.delete-title'),
      prompt: this.translate.instant('waters.fishing-spots.delete-confirm'),
    });
    if (!confirmed) return;

    this.isDeleting.set(true);
    try {
      await firstValueFrom(this.fishingSpotsService.deleteSpot(this.waterIdSig(), spotId));
      this.toastService.showSuccess(this.translate.instant('waters.fishing-spots.delete-success-form'));
      this.navigateBack();
    } catch {
      this.toastService.showError(this.translate.instant('waters.fishing-spots.delete-failed-form'));
    } finally {
      this.isDeleting.set(false);
    }
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    void this.router.navigate(['/waters/edit', this.waterIdSig()], { queryParams: { tab: 2 } });
  }
}
